/**
 * @file `SuppressionsPanel` — the `/emails/suppressions` route: the opt-out
 *       / bounce / complaint list, with an auto-exclusion note, reason
 *       filter chips, address search, and client-side pagination.
 * @module @labcraft/admin/ui/emails/suppressions-panel
 *
 * Read-only. The note makes the platform behaviour explicit — suppressed
 * addresses are removed from marketing broadcasts automatically, while an
 * individual send warns (the Part 3 opt-out floor) rather than silently
 * dropping. Filters + search narrow the list client-side; the page resets to
 * 1 whenever a filter changes.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react'
import { useSuppressions, usePagedList } from '../hooks/index.ts'
import { Pager } from '../kernel/pager.tsx'

const COLUMNS = ['Email', 'Reason', 'Campaign', 'Opted out'] as const
const REASONS = ['all', 'unsubscribe', 'bounce', 'complaint', 'manual'] as const
type ReasonFilter = (typeof REASONS)[number]

/** `2026-05-21T12:00:00Z` → `May 21, 2026`; invalid → em dash. */
function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Render the suppressions list route. */
export function SuppressionsPanel(): JSX.Element {
  const result = useSuppressions()
  const all = result.data?.suppressions ?? null
  const error = result.isError
    ? result.error.message || 'Could not load suppressions.'
    : null

  const [q, setQ] = useState('')
  const [reason, setReason] = useState<ReasonFilter>('all')
  const scrollRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (all ?? []).filter(
      s =>
        (reason === 'all' || s.reason === reason) &&
        (needle === '' || s.email.toLowerCase().includes(needle)),
    )
  }, [all, q, reason])

  const { page, setPage, resetPage, pageSize, rowHeight, offset, pageCount } =
    usePagedList(scrollRef, filtered.length)

  // Reset to the first page whenever a filter changes.
  useEffect(() => {
    resetPage()
  }, [q, reason, resetPage])

  const paged = filtered.slice(offset, offset + pageSize)

  return (
    <div className="panel emails-panel emails-panel--paged">
      <header className="panel-head">
        <div>
          <div className="panel-head__title-row">
            <h1 className="panel-head__title">Suppressions</h1>
            <span className="panel-head__count">
              {all ? `${filtered.length} shown` : '—'}
            </span>
          </div>
          <p className="panel-head__sub">
            // unsubscribes, bounces, complaints.
          </p>
        </div>
      </header>

      <div className="panel-banner">
        <b>Auto-excluded.</b> Suppressed addresses are removed from every
        marketing broadcast automatically. An individual send instead warns (or
        blocks a complaint) so you can make the call.
      </div>

      {error !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Could not load suppressions.</b> {error}
        </div>
      )}

      <div className="emails-subbar">
        <input
          className="lc-input emails-search"
          type="search"
          placeholder="search an address…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <div className="chips" role="group" aria-label="Filter by reason">
          {REASONS.map(r => (
            <button
              key={r}
              type="button"
              className="chip"
              aria-pressed={reason === r}
              onClick={() => setReason(r)}>
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="paged-list">
        <div
          className="table-scroll"
          ref={scrollRef}
          style={
            rowHeight > 0
              ? ({ '--row-h': `${rowHeight}px` } as CSSProperties)
              : undefined
          }>
          <table className="table">
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.isPending && all === null && (
                <tr>
                  <td className="empty-row" colSpan={COLUMNS.length}>
                    // loading…
                  </td>
                </tr>
              )}
              {all && filtered.length === 0 && (
                <tr>
                  <td className="empty-row" colSpan={COLUMNS.length}>
                    // no suppressions match.
                  </td>
                </tr>
              )}
              {paged.map(s => (
                <tr key={s.email}>
                  <td className="cell-email">{s.email}</td>
                  <td>
                    <span
                      className={`suppress-pill suppress-pill--${s.reason}`}>
                      {s.reason}
                    </span>
                  </td>
                  <td className="cell-meta cell-code">{s.campaignId ?? '—'}</td>
                  <td className="cell-meta">{formatDate(s.unsubscribedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="paged-foot">
          <Pager
            page={page}
            pageCount={pageCount}
            onPage={setPage}
            total={all ? filtered.length : undefined}
          />
        </div>
      </div>
    </div>
  )
}
